import type {
  IAssistantSummary,
  TAssistantKind,
  TAssistantOpName,
} from '@/interfaces/assistant.interface';
import { ofKind } from './card-kinds';
import { clampText, fitText } from './card-text';
import type { ICardFormat } from './card-text';

/** Callout titles hold at most this many characters (host contract §3.3). */
const TITLE_MAX = 160;

/**
 * The ops of a product's sizes (plan P4-1): their receipt names the product
 * whose sizes changed, whatever the number of rows (several of them on one
 * product make a mixed summary, named the same way).
 */
export const SIZE_CODES: ReadonlyArray<TAssistantOpName | 'mixed'> = [
  'addSizes',
  'updateSize',
  'removeSizes',
  'reorderSizes',
];

/**
 * «Se ocultó la categoría Postres.» / «Se mostraron 3 categorías.» (plan
 * P2-4): one category by its name, several counted. One whose name is not
 * known (its row could not be read), and categories shown and hidden in one
 * change, are the plain count of changes.
 */
function categoryAvailabilityTitle(
  summary: IAssistantSummary,
  count: number,
  name: string | null,
  fmt: ICardFormat
): string {
  const mixed = () =>
    clampText(fmt.tc('assistant.result.title.mixed', count), TITLE_MAX);
  if (summary.available === undefined) return mixed();
  const key = summary.available
    ? 'assistant.result.title.categoryShown'
    : 'assistant.result.title.categoryHidden';
  if (count !== 1) return clampText(fmt.tc(key, count), TITLE_MAX);
  if (!name) return mixed();
  return fitText(fmt, key, { name }, 'name', TITLE_MAX, count);
}

/** The receipt title of one kind's applied change (`appliedTitle`). */
type TAppliedTitle = (
  summary: IAssistantSummary,
  count: number,
  firstName: string | null,
  fmt: ICardFormat,
  sizesName: string | null
) => string;

/**
 * The receipt title of an applied change of the menu (plan §9.9), counted
 * with the rows that were applied: «Se cambió 1 precio en Bebidas.», «Se
 * ocultaron 3 productos.». `firstName` is the subject (a product, a
 * category) of a single-row change; `sizesName` the product whose sizes
 * every applied row changes (plan P4-1), which only the rows tell.
 */
function menuAppliedTitle(
  summary: IAssistantSummary,
  count: number,
  firstName: string | null,
  fmt: ICardFormat,
  sizesName: string | null
): string {
  const single = count === 1 && !!firstName;
  const name = firstName ?? '';
  const plural = (key: string) => clampText(fmt.tc(key, count), TITLE_MAX);
  // «Se cambiaron las variantes de Napolitana.» (plan P4-1), by one size op
  // or several (a mixed summary); sizes of several products, or of one the
  // receipt cannot name, are counted.
  const sizesCode = SIZE_CODES.includes(summary.code);
  if (sizesName && (sizesCode || summary.code === 'mixed'))
    return fitText(
      fmt,
      'assistant.result.title.sizes',
      { name: sizesName },
      'name',
      TITLE_MAX
    );
  if (sizesCode) return plural('assistant.result.title.mixed');
  switch (summary.code) {
    case 'setPrice':
    case 'adjustPrices':
    case 'setDiscount':
    case 'setPrices': {
      if (
        (summary.code === 'setPrice' || summary.code === 'setDiscount') &&
        single
      )
        return fitText(
          fmt,
          'assistant.result.title.price',
          { name },
          'name',
          TITLE_MAX
        );
      const categories = summary.categoryNames ?? [];
      if (categories.length === 1 && !summary.targetAll)
        return fitText(
          fmt,
          'assistant.result.title.prices',
          { category: categories[0] },
          'category',
          TITLE_MAX,
          count
        );
      return plural('assistant.result.title.pricesAll');
    }
    // Products shown and hidden in one change: the plain count of changes.
    case 'setAvailability':
      if (summary.available === undefined)
        return plural('assistant.result.title.mixed');
      return plural(
        summary.available
          ? 'assistant.result.title.shown'
          : 'assistant.result.title.hidden'
      );
    case 'setCategoryAvailability':
      return categoryAvailabilityTitle(summary, count, firstName, fmt);
    case 'moveItems':
      return plural('assistant.result.title.moved');
    // Copies are products added (plan P4-3).
    case 'createItem':
    case 'duplicateItem':
      return plural('assistant.result.title.created');
    case 'renameItem':
    case 'setDescription':
      return single
        ? fitText(
            fmt,
            'assistant.result.title.text',
            { name },
            'name',
            TITLE_MAX
          )
        : plural('assistant.result.title.mixed');
    // Option groups (plan P3-4); with other kinds (a copy, prices) the
    // summary is «mixed».
    case 'createOptionGroup':
    case 'updateOptionGroup':
    case 'linkOptionGroup':
    case 'unlinkOptionGroup':
    case 'reorderOptionGroups':
    case 'addOptionChoices':
    case 'updateOptionChoice':
    case 'removeOptionChoices':
    case 'reorderOptionChoices':
      return clampText(
        fmt.tc('assistant.result.title.options', count, {
          n: fmt.number(count),
        }),
        TITLE_MAX
      );
    default:
      return plural('assistant.result.title.mixed');
  }
}

/**
 * «Se aplicaron 3 cambios de inventario»: an inventory change counts its
 * changes of inventory, whatever its ops (plan P5-3).
 */
function stockAppliedTitle(
  _summary: IAssistantSummary,
  count: number,
  _firstName: string | null,
  fmt: ICardFormat
): string {
  return clampText(
    fmt.tc('assistant.result.title.stock', count, { n: fmt.number(count) }),
    TITLE_MAX
  );
}

const APPLIED_TITLES: Readonly<Record<TAssistantKind, TAppliedTitle>> = {
  'menu.changes': menuAppliedTitle,
  'stock.changes': stockAppliedTitle,
};

/**
 * The receipt title of an applied change of `kind` (plan §9.9, P5-3),
 * counted with the rows that were applied; `firstName` and `sizesName` as
 * `menuAppliedTitle` reads them.
 */
export function appliedTitle(
  summary: IAssistantSummary,
  count: number,
  firstName: string | null,
  fmt: ICardFormat,
  sizesName: string | null = null,
  kind: TAssistantKind = 'menu.changes'
): string {
  return ofKind(APPLIED_TITLES, kind)(
    summary,
    count,
    firstName,
    fmt,
    sizesName
  );
}
